import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { C, Btn } from "../components/UI";
import { supabase } from "../lib/supabase";

// Rotulos das marcacoes de valores, para exibir bonito na tela
const ROTULOS_VALORES = {
  equidade_salarial: "Equidade salarial",
  mulheres_lideranca: "Mulheres na lideranca",
  crescimento: "Crescimento",
  flexibilidade: "Flexibilidade",
  trabalho_hibrido: "Trabalho hibrido",
  bem_estar: "Bem-estar",
  maternidade: "Maternidade",
  familia: "Familia",
  ambiente_saudavel: "Ambiente saudavel"
};

const ESTILO_STATUS = {
  aprovado: { texto: "Aprovada", fundo: "#D1FAE5", cor: "#065F46" },
  pendente: { texto: "Pendente", fundo: "#FEF3C7", cor: "#92400E" },
  rejeitado: { texto: "Rejeitada", fundo: "#FEE2E2", cor: "#991B1B" }
};

function formatarData(valor) {
  if (!valor) return "";
  const d = new Date(valor);
  if (isNaN(d.getTime())) return "";
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  return `${dia}/${mes}/${d.getFullYear()}`;
}

function Estrelas({ nota }) {
  const n = Number(nota) || 0;
  return (
    <span style={{ fontSize: 16, letterSpacing: 1 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ color: i <= n ? "#F59E0B" : C.border }}>★</span>
      ))}
    </span>
  );
}

export default function MinhasAvaliacoes() {
  const navigate = useNavigate();

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [filtro, setFiltro] = useState("todas");

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setCarregando(true);
      setErro("");

      try {
        const { data: sessao, error: erroSessao } = await supabase.auth.getUser();
        if (erroSessao || !sessao || !sessao.user) {
          throw new Error("Voce precisa estar logada para ver suas avaliacoes.");
        }

        const { data: mulheres, error: erroMulher } = await supabase
          .from("mulheres")
          .select("id")
          .eq("user_id", sessao.user.id)
          .limit(1);
        if (erroMulher) throw erroMulher;
        if (!mulheres || mulheres.length === 0) {
          throw new Error("Nao encontramos seu perfil de usuaria.");
        }
        const mulherId = mulheres[0].id;

        const { data: linhas, error: erroAvaliacoes } = await supabase
          .from("avaliacoes")
          .select("id, created_at, empresa_id, cargo, periodo, nota_geral, comentario, valores, recomenda, status, motivo_rejeicao")
          .eq("mulher_id", mulherId)
          .order("created_at", { ascending: false });
        if (erroAvaliacoes) throw erroAvaliacoes;

        const avaliacoesCarregadas = linhas || [];

        // Busca o nome das empresas avaliadas
        const idsEmpresas = [...new Set(
          avaliacoesCarregadas.map(a => a.empresa_id).filter(Boolean)
        )];

        let nomesPorId = {};
        if (idsEmpresas.length > 0) {
          const { data: empresas, error: erroEmpresas } = await supabase
            .from("empresas")
            .select("id, nome")
            .in("id", idsEmpresas);
          if (erroEmpresas) throw erroEmpresas;
          (empresas || []).forEach(e => { nomesPorId[e.id] = e.nome; });
        }

        const resultado = avaliacoesCarregadas.map(a => ({
          ...a,
          empresaNome: nomesPorId[a.empresa_id] || "Empresa nao identificada"
        }));

        if (ativo) setAvaliacoes(resultado);
      } catch (e) {
        console.error("Erro ao carregar as avaliacoes:", e);
        if (ativo) {
          setErro((e && e.message) || "Nao conseguimos carregar suas avaliacoes agora.");
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => { ativo = false; };
  }, []);

  const total = avaliacoes.length;
  const aprovadas = avaliacoes.filter(a => a.status === "aprovado").length;
  const pendentes = avaliacoes.filter(a => a.status === "pendente").length;

  const visiveis = avaliacoes.filter(a => {
    if (filtro === "aprovadas") return a.status === "aprovado";
    if (filtro === "pendentes") return a.status === "pendente";
    return true;
  });

  const filtros = [
    { id: "todas", rotulo: "Todas" },
    { id: "aprovadas", rotulo: "Aprovadas" },
    { id: "pendentes", rotulo: "Pendentes" }
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.background }}>
      {/* Header */}
      <section style={{ background: C.roseMid, padding: "40px 20px", textAlign: "center" }}>
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 32,
          fontWeight: 700,
          color: "#fff",
          marginBottom: 10
        }}>
          Minhas Avaliacoes
        </h1>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
          Acompanhe suas avaliacoes de empresas
        </p>
      </section>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 20px 60px" }}>

        {carregando && (
          <p style={{ textAlign: "center", color: C.textSec, fontSize: 15 }}>
            Carregando suas avaliacoes...
          </p>
        )}

        {!carregando && erro && (
          <div style={{
            background: "#FEE2E2",
            border: "1px solid #FCA5A5",
            borderRadius: 12,
            padding: 20,
            textAlign: "left"
          }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#991B1B", marginBottom: 4 }}>
              Nao conseguimos carregar suas avaliacoes
            </p>
            <p style={{ fontSize: 13, color: "#991B1B" }}>{erro}</p>
          </div>
        )}

        {!carregando && !erro && (
          <>
            {/* Numeros */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              marginBottom: 32
            }}>
              {[
                { valor: total, rotulo: "Total de avaliacoes", cor: C.textMain },
                { valor: aprovadas, rotulo: "Aprovadas", cor: "#22C55E" },
                { valor: pendentes, rotulo: "Pendentes", cor: "#F59E0B" }
              ].map(card => (
                <div key={card.rotulo} style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 16,
                  padding: 24,
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: 30, fontWeight: 700, color: card.cor, marginBottom: 4 }}>
                    {card.valor}
                  </div>
                  <div style={{ fontSize: 13, color: C.textSec }}>{card.rotulo}</div>
                </div>
              ))}
            </div>

            {/* Filtros */}
            {total > 0 && (
              <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
                {filtros.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFiltro(f.id)}
                    style={{
                      padding: "8px 20px",
                      borderRadius: 20,
                      border: `1px solid ${filtro === f.id ? C.roseMid : C.border}`,
                      background: filtro === f.id ? C.roseMid : C.surface,
                      color: filtro === f.id ? "#fff" : C.textSec,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer"
                    }}
                  >
                    {f.rotulo}
                  </button>
                ))}
              </div>
            )}

            {/* Lista vazia */}
            {total === 0 && (
              <div style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 16,
                padding: 40,
                textAlign: "center"
              }}>
                <p style={{ fontSize: 16, color: C.textMain, marginBottom: 8 }}>
                  Voce ainda nao avaliou nenhuma empresa.
                </p>
                <p style={{ fontSize: 14, color: C.textSec, marginBottom: 24 }}>
                  Complete seu curriculo e avalie as empresas onde voce trabalhou.
                  Sua avaliacao e sempre anonima.
                </p>
                <Btn
                  variant="primary"
                  onClick={() => navigate("/completar-curriculo")}
                  style={{ padding: "12px 32px" }}
                >
                  Completar meu curriculo
                </Btn>
              </div>
            )}

            {/* Lista */}
            {visiveis.map(a => {
              const estilo = ESTILO_STATUS[a.status] || {
                texto: a.status || "Em analise",
                fundo: C.background,
                cor: C.textSec
              };
              const valores = Array.isArray(a.valores) ? a.valores : [];

              return (
                <div key={a.id} style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 16,
                  padding: 24,
                  marginBottom: 16
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 16,
                    marginBottom: 12
                  }}>
                    <div>
                      <h3 style={{ fontSize: 17, fontWeight: 600, color: C.textMain, marginBottom: 2 }}>
                        {a.empresaNome}
                      </h3>
                      <p style={{ fontSize: 13, color: C.textSec }}>
                        {a.cargo || "Cargo nao informado"}
                        {a.periodo ? ` · ${a.periodo}` : ""}
                      </p>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <Estrelas nota={a.nota_geral} />
                      <div style={{
                        display: "inline-block",
                        marginTop: 6,
                        background: estilo.fundo,
                        color: estilo.cor,
                        padding: "3px 12px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 500
                      }}>
                        {estilo.texto}
                      </div>
                    </div>
                  </div>

                  {a.comentario && (
                    <p style={{
                      fontSize: 14,
                      color: C.textSec,
                      fontStyle: "italic",
                      marginBottom: 12
                    }}>
                      "{a.comentario}"
                    </p>
                  )}

                  {valores.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                      {valores.map(v => (
                        <span key={v} style={{
                          background: "#FCE7F3",
                          color: C.roseMid,
                          padding: "4px 12px",
                          borderRadius: 20,
                          fontSize: 12
                        }}>
                          {ROTULOS_VALORES[v] || v}
                        </span>
                      ))}
                    </div>
                  )}

                  {a.status === "rejeitado" && a.motivo_rejeicao && (
                    <p style={{ fontSize: 13, color: "#991B1B", marginBottom: 12 }}>
                      Motivo: {a.motivo_rejeicao}
                    </p>
                  )}

                  <p style={{ fontSize: 12, color: C.textSec }}>
                    Avaliado em {formatarData(a.created_at)}
                    {a.status === "pendente" && " · aguardando aprovacao da equipe WoMom"}
                  </p>
                </div>
              );
            })}

            {total > 0 && visiveis.length === 0 && (
              <p style={{ textAlign: "center", color: C.textSec, fontSize: 14 }}>
                Nenhuma avaliacao neste filtro.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
